import debounce from "lodash.debounce";
import React from "react";
import { labels } from "../../../../localization/labels";
import EntityRatingService from "../../../../services/EntityRatingService";
import {
  activeFilterPageInfo,
  selectFilter
} from "../../../../services/FilteringUtils";
import KnowledgeCopyrightService from "../../../../services/KnowledgeCopyrightService";
import KnowledgeService from "../../../../services/KnowledgeService";
import KnowledgeTypeService from "../../../../services/KnowledgeTypeService";
import LifeCycleService from "../../../../services/LifeCycleService";
import UserService from "../../../../services/UserService";
import CommonUtil from "../../../../utils/CommonUtil";
import { constants } from "../../../../utils/Constants";
import PathUtil from "../../../../utils/PathUtil";
import SecurityUtils from "../../../../utils/SecurityUtils";
import { paths } from "../../../paths";
import PageComponent from "../../page/PageComponent";
import {
  knowledgeItemValidationRules as validationRules,
  validateEntity
} from "./../../../../validators/validationRules";
import Knowledgeitem from "./KnowledgeItem";

class KnowledgeItemContainer extends PageComponent {
  constructor(props) {
    super(props);

    this.knowledgeService = new KnowledgeService();
    this.knowledgeTypeService = new KnowledgeTypeService();
    this.lifeCycleService = new LifeCycleService();
    this.userService = new UserService();
    this.entityRatingsService = new EntityRatingService();
    this.knowledgeCopyrightService = new KnowledgeCopyrightService();
    this.state = {
      id: this.resolveItemId(),
      isChanged: false
    };
    this.state = this.initialState(this.state);
    this.sortInfo = {
      orderBy: "id",
      direction: "asc"
    };

    //delay of 500 ms(0.5s) on method for text input
    this.onChangeDebounced = debounce(this.onChangeDebounced, 500);
  }

  componentDidUpdate(prevProps) {
    const newId = this.resolveItemId();

    if (newId !== PathUtil.pathParameter(prevProps, "id")) {
      this.state.id = newId;

      this.setState(this.initialState(this.state));
      this.loadData();
    }

    let blocked = this.state.shouldBlockNavigation;
    if (this.state.isChanged) {
      blocked = CommonUtil.checkValuesChanged(
        this.state.oldKnowledgeItem,
        this.state.knowledgeItem
      );
      this.setState({ isChanged: false, shouldBlockNavigation: blocked });
    }

    if (blocked) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = undefined;
    }
  }

  componentDidMount() {
    this.loadData();
  }

  componentWillUnmount() {
    window.onbeforeunload = undefined;
  }

  initialState = state => {
    return {
      ...state,
      knowledgeItem: {},
      oldKnowledgeItem: undefined,
      knowledgeTypes: [],
      knowledgeLifeCycles: [],
      lifeCycleHistoryLogs: [],
      knowledgeRatings: {},
      disabled: true,
      isNew: true,
      rating: {},
      shouldBlockNavigation: false,
      editable: true,
      lifeCycleEditable: false
    };
  };

  loadData = () => {
    if (this.state.id) {
      this.loadKnowledgeItem(this.state.id);
    } else {
      this.setComponentTitle(labels.knowledgeItem);
      this.setState({
        classificationsFilter: SecurityUtils.geClassificationsByOperation(
          constants.OPERATION_CREATE
        )
      });
    }

    this.loadKnowledgeTypes();
  };

  getComponentTitle = () => {
    return this.state.knowledgeItem ? this.state.knowledgeItem.name : undefined;
  };

  loadKnowledgeItem = id => {
    this.prepareCall(
      this.knowledgeService.getKnowledgeItem(
        id,
        this.onKnowledgeItemLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onKnowledgeItemLoad = data => {
    const editable = SecurityUtils.userHasPermissionOnEntity(
      data,
      constants.OPERATION_PATCH
    );
    const lifeCycleEditable = SecurityUtils.userHasPermissionOnEntity(
      data,
      constants.OPERATION_CHANGE_LIFECYCLE
    );
    const operation = editable
      ? constants.OPERATION_PATCH
      : constants.OPERATION_READ;
    this.setState({
      classificationsFilter: SecurityUtils.geClassificationsByOperation(
        operation
      )
    });
    this.setState({
      knowledgeItem: { ...data },
      oldKnowledgeItem: { ...data },
      disabled: false,
      isNew: false,
      editable: editable,
      lifeCycleEditable: lifeCycleEditable
    });
    this.setComponentTitle(this.state.knowledgeItem.name, true);
    this.loadInitialKnowledgeCopyright(data);
    this.loadKnowledgeType(data);
    this.loadKnowledgeLifeCycle(data);
    this.loadRecordedBy(data);
    this.loadRatings();
    this.loadUsersEntityRating(data);
  };

  loadInitialKnowledgeCopyright = data => {
    const url = data._links.knowledgeCopyrights.href;
    this.prepareCall(
      this.knowledgeCopyrightService.getEntitiesByUrl(
        url,
        this.onInitialKnowledgeCopyrightLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onInitialKnowledgeCopyrightLoad = data => {
    let knowledgeCopyright;
    const dataContent = CommonUtil.getDataContent(data);
    if (dataContent) {
      knowledgeCopyright = dataContent[0];
    }
    this.setInitialEntityPropertyState(
      "knowledgeCopyright",
      knowledgeCopyright
    );
  };

  loadRecordedBy = data => {
    const url = data._links.recordedBy.href;
    this.loadParty(url, this.onRecordedByLoad);
  };

  loadParty = (url, callback) => {
    this.prepareCall(
      this.userService.getEntitiesByUrl(
        url,
        callback,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadKnowledgeType = data => {
    const url = data._links.knowledgeTypes.href;
    this.prepareCall(
      this.knowledgeTypeService.getEntitiesByUrl(
        url,
        this.onKnowledgeTypeLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadKnowledgeLifeCycle = data => {
    const url = data._links.lifeCycles.href;
    this.prepareCall(
      this.lifeCycleService.getEntitiesWithParams(
        url,
        undefined,
        undefined,
        this.onKnowledgeLifeCycleLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadKnowledgeTypes = () => {
    this.prepareCall(
      this.knowledgeTypeService.getEntities(
        activeFilterPageInfo,
        this.sortInfo,
        this.onKnowledgeTypesLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadKnowledgeLifeCycles = knowledgeLifeCycle => {
    const url = knowledgeLifeCycle._links.nextLifeCycles.href;
    this.prepareCall(
      this.lifeCycleService.getEntitiesWithParams(
        url,
        undefined,
        undefined,
        this.onKnowledgeLifeCyclesLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadRatings = () => {
    const url = this.state.knowledgeItem._links.calculateRatings.href;
    this.prepareCall(
      this.knowledgeService.post(
        url,
        undefined,
        this.onRatingsLoaded,
        undefined,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  loadUsersEntityRating = data => {
    const entityRatingFilterInfo = {
      filters: {
        entityId: selectFilter(data.id)
      }
    };

    this.prepareCall(
      this.entityRatingsService.getEntityRatings(
        entityRatingFilterInfo,
        undefined,
        this.onUsersEntityRatingLoaded,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onUsersEntityRatingLoaded = data => {
    if (
      data &&
      data._embedded &&
      data._embedded.content &&
      data._embedded.content[0]
    ) {
      this.setState({
        rating: data._embedded.content[0].rating
      });
    }
  };

  onRatingsLoaded = data => {
    const ratings = data ? data : {};
    this.setState({ knowledgeRatings: ratings });
  };

  onKnowledgeTypesLoad = data => {
    const knowledgeTypes = data._embedded ? data._embedded.content : [];
    this.setState({ knowledgeTypes: knowledgeTypes });
  };

  onKnowledgeLifeCyclesLoad = data => {
    var nextKnowledgeLifeCycles = [];
    if (data._embedded !== undefined) {
      nextKnowledgeLifeCycles = data._embedded.content;
    }
    const { knowledgeItem } = this.state;
    const currentKnowledgeLifeCycle = knowledgeItem.lifeCycle;
    nextKnowledgeLifeCycles.push(currentKnowledgeLifeCycle);
    this.setState({ knowledgeLifeCycles: nextKnowledgeLifeCycles });
  };

  onKnowledgeTypeLoad = data => {
    const knowledgeType = data._embedded.content[0];
    const { knowledgeItem, oldKnowledgeItem } = this.state;
    knowledgeItem.knowledgeType = knowledgeType;
    oldKnowledgeItem.knowledgeType = knowledgeType;
    this.setState({
      knowledgeItem: knowledgeItem,
      oldKnowledgeItem: oldKnowledgeItem
    });
  };

  onKnowledgeLifeCycleLoad = data => {
    const dataContent = CommonUtil.getDataContent(data);
    if (dataContent) {
      const knowledgeLifeCycle = dataContent[0];
      const { knowledgeItem, oldKnowledgeItem } = this.state;
      knowledgeItem.lifeCycle = knowledgeLifeCycle;
      oldKnowledgeItem.lifeCycle = knowledgeLifeCycle;
      this.setState({
        knowledgeItem: knowledgeItem,
        oldKnowledgeItem: oldKnowledgeItem
      });
      this.loadKnowledgeLifeCycles(knowledgeLifeCycle);
    }
  };

  onRecordedByLoad = data => {
    const { knowledgeItem } = this.state;
    knowledgeItem.recordedBy = data;
    this.setState({
      knowledgeItem: knowledgeItem
    });
  };

  onChangeDebounced = knowledgeItem => {
    this.onChangeValidation(knowledgeItem);
  };

  onChangeValidation = knowledgeItem => {
    const disabled = !this.validator(knowledgeItem);
    this.setState({ isChanged: true, disabled: disabled });
  };

  validator = entity => {
    return validateEntity(entity, validationRules.knowledgeItem);
  };

  onChange = event => {
    var knowledgeItem = this.state.knowledgeItem;
    knowledgeItem[event.target.id] = event.target.value;
    this.setState({
      knowledgeItem: knowledgeItem
    });

    if (typeof event.target.value === "object") {
      this.onChangeValidation(knowledgeItem);
    } else {
      this.onChangeDebounced(knowledgeItem);
    }
  };

  onRatingChange = rating => {
    this.setState({
      rating: rating
    });
  };

  onSave = () => {
    const { knowledgeItem, oldKnowledgeItem } = this.state;
    if (knowledgeItem && knowledgeItem.id) {
      this.prepareCall(
        this.knowledgeService.patchEntity(
          knowledgeItem,
          oldKnowledgeItem,
          this.onDataResponse,
          this.messageHandler.handleError,
          this.onSaveSuccessMessage
        )
      );
      this.performCalls();
    } else {
      this.prepareCall(
        this.knowledgeService.postEntity(
          knowledgeItem,
          this.onDataResponse,
          this.messageHandler.handleError,
          this.onSaveSuccessMessage
        )
      );
      this.performCalls();
    }
  };

  onDataResponse = data => {
    this.setState({ shouldBlockNavigation: false });
    if (this.state.id) {
      this.loadKnowledgeItem(this.state.id);
    } else {
      this.changePath(
        PathUtil.itemPath(paths.KNOWLEDGE_ITEM, data.id),
        data.name
      );
    }
  };

  onRate = () => {
    const entityRating = {
      entity: {
        id: this.state.knowledgeItem.id,
        type: this.state.knowledgeItem.type
      },
      rating: this.state.rating
    };
    this.prepareCall(
      this.entityRatingsService.postEntityRating(
        entityRating,
        this.loadRatings,
        this.onSaveSuccessMessage,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  setInitialEntityPropertyState = (propertyName, initialPropertyState) => {
    const { knowledgeItem, oldKnowledgeItem } = this.state;
    knowledgeItem[propertyName] = initialPropertyState;
    oldKnowledgeItem[propertyName] = initialPropertyState;
    this.setState({
      knowledgeItem: knowledgeItem,
      oldKnowledgeItem: oldKnowledgeItem
    });
  };

  setClassificationsOnEntity = classifications => {
    let knowledgeItem = this.state.knowledgeItem;
    knowledgeItem.classifications = classifications;
    this.setState({
      knowledgeItem,
      isChanged: true
    });
  };

  //TODO - ovo je hack zbog patch-a. Ako se ne zove ta metoda, ne radi dodavanje izvora znanja kada je već izabran neki izvor znanja
  resetKnowledgeSourceOnOldItem = () => {
    let oldKnowledgeItem = this.state.oldKnowledgeItem;
    if (oldKnowledgeItem) {
      oldKnowledgeItem.knowledgeSource = undefined;
      this.setState({ oldKnowledgeItem });
    }
  };

  render() {
    return (
      <Knowledgeitem
        knowledgeItem={this.state.knowledgeItem}
        onChange={this.onChange}
        onSave={this.onSave}
        knowledgeTypes={this.state.knowledgeTypes}
        knowledgeLifeCycles={this.state.knowledgeLifeCycles}
        validationRules={validationRules.knowledgeItem}
        disabled={this.state.disabled}
        isNew={this.state.isNew}
        {...this.handlerProps()}
        lifeCycleHistoryLogs={this.state.lifeCycleHistoryLogs}
        knowledgeRatings={this.state.knowledgeRatings}
        rating={this.state.rating}
        onRatingChange={this.onRatingChange}
        onRate={this.onRate}
        shouldBlockNavigation={this.state.shouldBlockNavigation}
        setInitialEntityPropertyState={this.setInitialEntityPropertyState}
        loadParty={this.loadParty}
        setClassificationsOnEntity={this.setClassificationsOnEntity}
        editable={this.state.editable}
        lifeCycleEditable={this.state.lifeCycleEditable}
        classificationsFilter={this.state.classificationsFilter}
        //TODO - ovo je hack zbog patch-a. Ako se ne zove ta metoda, ne radi dodavanje izvora znanja kada je već izabran neki izvor znanja
        resetKnowledgeSourceOnOldItem={this.resetKnowledgeSourceOnOldItem}
      />
    );
  }
}

export default KnowledgeItemContainer;
